/*
 * Privacy Policy — public page at /privacy-policy (alias /privacy).
 *
 * 2026-09-27 refresh: text revised by an AI assistant alongside the new
 * Terms of Service (TermsPage.js). It corrected statements that had become
 * false (e.g. "we do not use advertising or tracking cookies" — the site now
 * runs Google Analytics and the Google Ads tag behind Consent Mode; DocuSeal
 * was listed but is not used; Settings has "Request Account Deletion", not
 * "Delete My Account"). It should be reviewed by a solicitor before the first
 * paying customer, together with the Terms.
 *
 * This file also exports the shared legal-page layout (LegalPage, P, Bullets,
 * DataTable, InlineLink) that TermsPage.js imports, so the two documents stay
 * visually identical and print the same way.
 */
import React, { useEffect } from 'react';
import { Box, Typography, Link as MuiLink, Container } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import usePageMeta from '../../utils/pageMeta';

// ── Palette (all pairs checked for WCAG AA on their background) ──────────
// ink #0F1117 / body #374151 on #FFFFFF ≥ 10:1; muted #4B5563 on #F8F9FB ≈ 7:1;
// link #B42330 on #FFFFFF ≈ 6.4:1.
const INK = '#0F1117';
const BODY = '#374151';
const MUTED = '#4B5563';
const LINK = '#B42330';
const PAGE_BG = '#F8F9FB';
const BORDER = '#E5E7EB';

const printHide = { '@media print': { display: 'none !important' } };

export const P = ({ children, sx }) => (
  <Typography sx={{ fontSize: '0.9375rem', color: BODY, lineHeight: 1.7, mb: 1.5, ...sx }}>{children}</Typography>
);

export const Bullets = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 1.5 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ mb: 0.5, color: BODY }}>
        <Typography component="span" sx={{ fontSize: '0.9375rem', color: BODY, lineHeight: 1.7 }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

/** Internal route or external/mailto link, styled consistently. */
export const InlineLink = ({ to, href, children }) => {
  const sx = { color: LINK, textDecoration: 'underline', fontWeight: 500, '&:hover': { color: '#8E1B25' } };
  if (to) return <MuiLink component={RouterLink} to={to} sx={sx}>{children}</MuiLink>;
  const external = href && href.startsWith('http');
  return (
    <MuiLink href={href} sx={sx} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {children}
    </MuiLink>
  );
};

/**
 * Simple responsive table. On narrow screens the table scrolls inside its
 * own container (never the page) — `tabIndex=0` + a label make that scroll
 * region keyboard-reachable.
 */
export const DataTable = ({ caption, columns, rows }) => (
  <Box
    role="region"
    aria-label={caption}
    tabIndex={0}
    sx={{ overflowX: 'auto', mb: 2, border: `1px solid ${BORDER}`, borderRadius: '8px', '&:focus-visible': { outline: `2px solid ${LINK}`, outlineOffset: 2 } }}
  >
    <Box component="table" sx={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: '0.875rem' }}>
      <Box component="caption" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{caption}</Box>
      <thead>
        <tr>
          {columns.map((c) => (
            <Box component="th" scope="col" key={c} sx={{ textAlign: 'left', p: 1.25, backgroundColor: '#F3F4F6', color: INK, fontWeight: 700, borderBottom: `1px solid ${BORDER}` }}>{c}</Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <Box component={j === 0 ? 'th' : 'td'} scope={j === 0 ? 'row' : undefined} key={j}
                sx={{ textAlign: 'left', verticalAlign: 'top', p: 1.25, color: j === 0 ? INK : BODY, fontWeight: j === 0 ? 600 : 400, lineHeight: 1.55, borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${BORDER}` }}>
                {cell}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
);

/**
 * Shared shell for the legal documents: public header, one H1, last-updated
 * line, table of contents, numbered sections, footer. `sections` is an array
 * of { id, title, body } so the TOC and the headings cannot drift apart.
 */
export function LegalPage({ title, lastUpdated, intro, sections }) {
  const { hash } = useLocation();

  // Deep links (/terms#bank-connection) arrive before the content has
  // rendered, so the browser's own jump-to-anchor misses. Redo it once
  // the sections exist.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView();
  }, [hash]);

  const navLinkSx = { color: MUTED, fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none', minHeight: 44, display: 'inline-flex', alignItems: 'center', '&:hover': { color: INK, textDecoration: 'underline' } };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: PAGE_BG, '@media print': { backgroundColor: '#fff' } }}>
      {/* ── Header ─────────────────────────────────────── */}
      <Box component="header" sx={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDER}`, ...printHide }}>
        <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, gap: 2 }}>
          <MuiLink component={RouterLink} to="/" underline="none" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 44 }} aria-label="NestHub home">
            <Box aria-hidden sx={{ width: 30, height: 30, borderRadius: '8px', backgroundColor: '#C8303C', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>N</Box>
            <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.0625rem', color: INK }}>NestHub</Typography>
          </MuiLink>
          <Box component="nav" aria-label="Site" sx={{ display: 'flex', gap: { xs: 2, sm: 3 } }}>
            <MuiLink component={RouterLink} to="/pricing" sx={navLinkSx}>Pricing</MuiLink>
            <MuiLink component={RouterLink} to="/login" sx={navLinkSx}>Sign in</MuiLink>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: INK, mb: 1, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>{title}</Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>Last updated {lastUpdated}</Typography>
        </Box>

        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '12px', p: { xs: 2.5, md: 4 }, '@media print': { border: 'none', p: 0 } }}>
          {intro}

          {/* ── Table of contents ──────────────────────── */}
          <Box component="nav" aria-labelledby="toc-heading" sx={{ backgroundColor: PAGE_BG, border: `1px solid ${BORDER}`, borderRadius: '8px', p: 2, my: 3, '@media print': { backgroundColor: '#fff' } }}>
            <Typography id="toc-heading" component="h2" sx={{ fontWeight: 700, color: INK, fontSize: '1rem', mb: 1 }}>Contents</Typography>
            <Box component="ol" sx={{ pl: 3, m: 0, columns: { xs: 1, sm: 2 }, columnGap: 4 }}>
              {sections.map((s) => (
                <Box component="li" key={s.id} sx={{ mb: 0.5, breakInside: 'avoid', color: BODY, fontSize: '0.9rem' }}>
                  <MuiLink href={`#${s.id}`} sx={{ color: LINK, fontSize: '0.9rem', lineHeight: 1.8, textDecoration: 'underline' }}>{s.title}</MuiLink>
                </Box>
              ))}
            </Box>
          </Box>

          {sections.map((s, i) => (
            <Box component="section" key={s.id} id={s.id} aria-labelledby={`${s.id}-h`}
              sx={{ scrollMarginTop: 16, pt: 3, mt: i === 0 ? 0 : 3, borderTop: i === 0 ? 'none' : `1px solid ${BORDER}` }}>
              <Typography id={`${s.id}-h`} variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.5, color: INK, '@media print': { breakAfter: 'avoid' } }}>
                {i + 1}. {s.title}
              </Typography>
              {s.body}
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── Footer ─────────────────────────────────────── */}
      <Box component="footer" sx={{ borderTop: `1px solid ${BORDER}`, backgroundColor: '#fff', ...printHide }}>
        <Container maxWidth="md" sx={{ py: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Typography sx={{ fontSize: '0.8125rem', color: MUTED }}>
            © {new Date().getFullYear()} Linehan O'Brien Investments Ltd, trading as NestHub
          </Typography>
          <Box component="nav" aria-label="Legal" sx={{ display: 'flex', gap: 2.5 }}>
            <MuiLink component={RouterLink} to="/privacy" sx={{ ...navLinkSx, fontSize: '0.8125rem' }}>Privacy</MuiLink>
            <MuiLink component={RouterLink} to="/terms" sx={{ ...navLinkSx, fontSize: '0.8125rem' }}>Terms</MuiLink>
            <MuiLink href="mailto:support@nesthub.ie" sx={{ ...navLinkSx, fontSize: '0.8125rem' }}>Contact</MuiLink>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

// ── Privacy Policy content ────────────────────────────────────────────────

const CONTACT = <InlineLink href="mailto:support@nesthub.ie">support@nesthub.ie</InlineLink>;

const SUBPROCESSORS = [
  ['Google Ireland Ltd (Firebase / Google Cloud)', 'Hosting, database, file storage, sign-in, server functions, server logs', 'EU. Our server functions run in europe-west1 (Belgium). Google may access data from outside the EEA for support and security (see section 8).', 'Always'],
  ['Stripe Payments Europe, Ltd', 'Subscription payments and invoices. Card details go directly to Stripe; we never see or store them.', 'EU, with some processing by Stripe, Inc. in the US', 'When you subscribe'],
  ['Twilio Inc. (SendGrid)', 'Sending transactional email: welcome, rent reminders, notices, signing requests, maintenance updates, monthly summaries', 'US', 'Always'],
  ['Twilio Inc. (Programmable SMS)', 'Urgent maintenance text alerts to phone numbers your organisation has set up', 'US', 'Only if you use SMS alerts'],
  ['Enable Banking Oy', 'Read-only bank account information for rent matching (section 6)', 'Finland (EU)', 'Only if a landlord connects a bank'],
  ['Anthropic PBC', 'The "Suggest with AI" button on a maintenance issue: the issue title, description and any photos you attach are sent to generate a suggested category and priority', 'US', 'Only when you press "Suggest with AI"'],
  ['Google Ireland Ltd (Google Analytics / Google Ads)', 'Measuring site use and whether our ads lead to sign-ups', 'EU and US', 'Only if you accept analytics cookies'],
];

const COOKIES = [
  ['Essential', 'Keeping you signed in (Firebase Authentication, stored in your browser\'s local storage), remembering your cookie choice, security and fraud checks, keeping an unfinished sign-up form so you do not lose it', 'No — needed for the service to work'],
  ['Analytics', 'Google Analytics (via Firebase) cookies such as _ga, used to understand which pages and features are used', 'Yes — only after you click "Accept all"'],
  ['Advertising measurement', 'Google Ads cookies such as _gcl_au, used to tell whether a visit from one of our ads led to a sign-up', 'Yes — only after you click "Accept all"'],
];

const PRIVACY_SECTIONS = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    body: (
      <>
        <P>NestHub is property management software operated by Linehan O'Brien Investments Ltd ("we", "us", "our"), a company registered in Ireland, based in Dublin.</P>
        <P>Under the General Data Protection Regulation (EU) 2016/679 ("GDPR") and the Data Protection Act 2018 we act as:</P>
        <Bullets items={[
          <><strong>Controller</strong> for information about our customers — the landlords, letting agents and their staff who hold NestHub accounts — and about visitors to our website.</>,
          <><strong>Processor</strong> for the personal data our customers put into NestHub about their tenants, applicants, guarantors and contractors. We process that data only on the customer's instructions. The landlord is the controller, and tenants should contact their landlord first about it.</>,
        ]} />
        <P>Data protection contact: {CONTACT} (please put "Data protection" in the subject line).</P>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: 'What data we collect',
    body: (
      <>
        <P><strong>Account data (controller):</strong> name, email address, password (stored only as a secure hash by Firebase Authentication), organisation name, phone number if you add one, plan and subscription status, two-factor authentication settings if you turn it on. Payment card details are collected by Stripe; we do not see or store them.</P>
        <P><strong>Customer data (processor, on the landlord's behalf):</strong> tenant names, email addresses and phone numbers; property addresses; rent and deposit amounts; tenancy dates; payment history; maintenance reports and photos; documents such as licence and tenancy agreements; electronic signatures; notices; messages between landlord and tenant.</P>
        <P><strong>Bank data (processor, only if the landlord connects a bank):</strong> see <InlineLink href="#bank-data">section 6</InlineLink>.</P>
        <P><strong>Usage and device data:</strong> pages visited, features used, browser and device type, and IP address. Server logs are used for security and troubleshooting. Analytics data is only collected with your consent (<InlineLink href="#cookies">section 11</InlineLink>).</P>
        <P><strong>Error reports:</strong> technical reports when something breaks in your browser — see <InlineLink href="#error-reports">section 7</InlineLink>.</P>
        <P><strong>Communications:</strong> support requests, emails you send us, and records of the transactional emails we send on your behalf (recipient, type, date and delivery status).</P>
        <P><strong>Optional feedback:</strong> if you answer the short "why didn't you sign up?" question on our website, we store your answer and, only if you choose to give it, your email address.</P>
      </>
    ),
  },
  {
    id: 'legal-basis',
    title: 'Legal basis for processing',
    body: (
      <>
        <Bullets items={[
          <><strong>Contract</strong> (Art. 6(1)(b)): running your account and providing the service you signed up for.</>,
          <><strong>Legitimate interests</strong> (Art. 6(1)(f)): keeping the service secure, preventing fraud and abuse, fixing errors, and answering feedback you choose to send us.</>,
          <><strong>Legal obligation</strong> (Art. 6(1)(c)): keeping billing and accounting records as Irish tax law requires.</>,
          <><strong>Consent</strong> (Art. 6(1)(a)): analytics and advertising-measurement cookies. You can withdraw consent at any time.</>,
        ]} />
        <P>For customer data we process on a landlord's behalf, the landlord (as controller) is responsible for having a valid legal basis for collecting that data and putting it into NestHub.</P>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How we use your data',
    body: (
      <Bullets items={[
        'To provide, maintain and secure NestHub',
        'To take subscription payments through Stripe',
        'To send the transactional emails the service depends on (sign-up and verification, rent reminders, signing requests, notices, maintenance updates, monthly summaries, trial reminders)',
        'To generate documents such as licence and tenancy agreements',
        'To match incoming bank payments to rent, where the landlord has connected a bank',
        'To provide customer support',
        'To find and fix errors, and — with your consent — to understand how the website and product are used',
        'To comply with our legal obligations',
      ]} />
    ),
  },
  {
    id: 'sub-processors',
    title: 'Who we share data with (sub-processors)',
    body: (
      <>
        <P>We use the following service providers to run NestHub. Each processes personal data only to provide its service to us, under a data processing agreement.</P>
        <DataTable
          caption="NestHub sub-processors"
          columns={['Provider', 'What for', 'Where', 'When']}
          rows={SUBPROCESSORS}
        />
        <P>We do not sell, rent or trade personal data. We never use tenant data for advertising. We may disclose data where the law requires it, for example in response to a valid court order.</P>
        <P>If we add or replace a sub-processor, we will update this list and tell account holders by email before the change takes effect (see the <InlineLink to="/terms#data-processing">Terms of Service</InlineLink>).</P>
      </>
    ),
  },
  {
    id: 'bank-data',
    title: 'Bank account information',
    body: (
      <>
        <P>Landlords can choose to connect a bank account so that NestHub can spot incoming rent payments. This is optional and off until a landlord turns it on.</P>
        <Bullets items={[
          <><strong>How it works:</strong> the connection is provided by Enable Banking Oy, a Finnish company regulated as an account information service provider. You log in and give consent at your own bank. NestHub never sees your banking username, password or security codes.</>,
          <><strong>What is read:</strong> the account name and identifiers of the accounts you choose, and their transactions — date, amount, description or reference, and the payer's name and IBAN where the bank provides them. Access is read-only: NestHub cannot make payments or move money.</>,
          <><strong>Why:</strong> only to match payments to tenants' rent.</>,
          <><strong>When:</strong> NestHub checks for new transactions automatically once a day, and whenever you press Sync.</>,
          <><strong>What we keep:</strong> the connection record (bank name, the accounts you connected, consent expiry date); payments that are matched and recorded as rent (date, amount, payer name and IBAN, bank reference); payer names you confirm so future payments match automatically; and, until you deal with them, proposed matches and a limited number of recent unmatched incoming payments for you to review. We also note which payments you dismiss so they are not suggested again. Other transactions — outgoing payments, internal transfers and anything that could not be rent — are not stored.</>,
          <><strong>How long:</strong> bank consent lasts up to 180 days, after which you must reconnect. Recorded rent payments are kept with the rest of your account data (<InlineLink href="#retention">section 9</InlineLink>).</>,
          <><strong>How to disconnect:</strong> in NestHub, open Bank Matching and choose Disconnect. This revokes the session with Enable Banking and deletes our access tokens. You can also withdraw consent directly with your bank at any time.</>,
        ]} />
      </>
    ),
  },
  {
    id: 'error-reports',
    title: 'Error reports',
    body: (
      <>
        <P>When something goes wrong in your browser, NestHub sends us a short technical error report so we can fix it. A report contains the error message and technical trace, the page path (without any query string or link codes), your browser type, the app version and, if you are signed in, your user and organisation identifiers.</P>
        <P>Error reports never include what you typed into forms, email addresses, passwords or tokens. They are grouped by error and day, are only readable by NestHub staff, and are kept only as long as they are useful for fixing problems. Our legal basis is our legitimate interest in keeping the service working.</P>
      </>
    ),
  },
  {
    id: 'international-transfers',
    title: 'International data transfers',
    body: (
      <P>We keep our main database and files in Google's EU regions. Where a sub-processor processes data outside the European Economic Area (for example Twilio SendGrid, Anthropic, and parts of Stripe and Google), the transfer is protected by the European Commission's Standard Contractual Clauses (SCCs) and, where the provider is certified, the EU-US Data Privacy Framework. You can ask us for more detail at {CONTACT}.</P>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep data',
    body: (
      <>
        <Bullets items={[
          <><strong>Account and customer data:</strong> for as long as the account is open. Landlords can edit or delete their own tenant records at any time.</>,
          <><strong>After an account is closed:</strong> we delete account and customer data within 90 days, giving the landlord the chance to export it first. The landlord remains responsible for keeping any records they are legally required to hold (for example tenancy or tax records).</>,
          <><strong>Our billing records:</strong> 6 years, as Irish tax law requires.</>,
          <><strong>Bank connection tokens:</strong> deleted when you disconnect, or unusable once the bank consent expires (at most 180 days).</>,
          <><strong>Server logs:</strong> about 30 days.</>,
          <><strong>Error reports:</strong> as long as needed to fix the problem.</>,
          <><strong>Website feedback:</strong> 12 months.</>,
          <><strong>Support emails:</strong> 2 years after the conversation ends.</>,
        ]} />
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    body: (
      <>
        <P>Under the GDPR you have the right to:</P>
        <Bullets items={[
          'access the personal data we hold about you',
          'have inaccurate data corrected (you can edit most of it yourself in Settings)',
          'have your data erased, subject to legal retention obligations',
          'restrict processing while a complaint is resolved',
          'receive your data in a portable, machine-readable format',
          'object to processing based on legitimate interests',
          'withdraw consent at any time, where processing is based on consent',
        ]} />
        <P><strong>How to exercise them:</strong> account holders can use "Export My Data" (a JSON file) and "Request Account Deletion" in Settings → Full data &amp; privacy. For anything else, email {CONTACT}. We will reply within one month. We may need to confirm your identity first.</P>
        <P><strong>Tenants:</strong> your landlord controls the data they hold about you in NestHub, so please contact them first. If you contact us, we will pass your request to your landlord and help them answer it.</P>
        <P><strong>Complaints:</strong> you can complain to the Data Protection Commission, the Irish supervisory authority, at <InlineLink href="https://www.dataprotection.ie">www.dataprotection.ie</InlineLink>. We would appreciate the chance to sort out your concern first.</P>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and similar technologies',
    body: (
      <>
        <P>We use a small number of cookies and browser storage items. Non-essential ones are only used if you click "Accept all" on our cookie banner.</P>
        <DataTable
          caption="Cookies used by NestHub"
          columns={['Type', 'What for', 'Consent needed?']}
          rows={COOKIES}
        />
        <P>Until you accept, our Google tag runs in "consent denied" mode and does not set or read analytics or advertising cookies. Google may still receive limited, cookieless technical signals (for example that a page loaded), as part of Google Consent Mode.</P>
        <P>
          To change your choice at any time,{' '}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('nesthub:cookie-settings'))}
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            open the cookie settings
          </button>
          .
        </P>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Data security',
    body: (
      <>
        <P>We use appropriate technical and organisational measures to protect personal data, including:</P>
        <Bullets items={[
          'encryption in transit (HTTPS) and at rest (Google Cloud storage encryption)',
          'separation of each organisation\'s data, enforced by database and file-storage security rules',
          'role-based access for landlords, staff and tenants',
          'secure password hashing through Firebase Authentication, with optional two-factor authentication',
          'automatic sign-out after a period of inactivity',
          'secrets such as API keys held in Google Secret Manager, not in our code',
        ]} />
        <P>No system is perfectly secure. If a personal data breach occurs, we will notify affected customers and, where required, the Data Protection Commission.</P>
      </>
    ),
  },
  {
    id: 'processing-agreement',
    title: 'Data processing agreement',
    body: (
      <P>When a landlord uses NestHub to manage tenant data, we act as their processor under Article 28 of the GDPR. Our processor commitments — acting only on the landlord's instructions, confidentiality, security, sub-processors, help with data subject requests, deletion or return of data, and breach notification — are set out in the <InlineLink to="/terms#data-processing">Data processing section of our Terms of Service</InlineLink>, which forms our data processing agreement. You can request a signed copy at {CONTACT}.</P>
    ),
  },
  {
    id: 'children',
    title: "Children's data",
    body: (
      <P>NestHub is not intended for anyone under 18. We do not knowingly collect personal data from children.</P>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <P>We may update this policy from time to time. We will email account holders about material changes before they take effect. The date at the top of this page shows when it was last revised.</P>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    body: (
      <Bullets items={[
        <>Email: {CONTACT}</>,
        <>Post: Linehan O'Brien Investments Ltd, Dublin, Ireland</>,
        <>Data Protection Commission: <InlineLink href="https://www.dataprotection.ie">www.dataprotection.ie</InlineLink></>,
      ]} />
    ),
  },
];

export default function PrivacyPolicyPage() {
  // Legal page: indexable (linked from the cookie banner and the footer,
  // and Google expects a reachable privacy policy for an ads account) with
  // its own title and canonical. /privacy is the canonical; /privacy-policy
  // renders the same component and points here.
  usePageMeta({
    title: 'Privacy Policy | NestHub',
    description: 'How NestHub collects, uses and protects landlord and tenant data under GDPR: sub-processors, bank data, cookies, retention and your rights.',
    canonical: '/privacy',
  });

  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="27 September 2026"
      intro={<P sx={{ mb: 0 }}>This policy explains what personal data NestHub collects, why, who we share it with, and the rights you have. It applies to our website and to the NestHub app. Please also read our <InlineLink to="/terms">Terms of Service</InlineLink>.</P>}
      sections={PRIVACY_SECTIONS}
    />
  );
}
